import { useEffect } from "react";

export interface SeoData {
  title: string;
  description?: string;
  keywords?: string;
  path?: string;
  type?: string;
  image?: string;
}

// The real site origin is provided by the build environment.
const FALLBACK_SITE_URL = "https://allchinabuyspread.rest";

// Social share image reused from the platform-managed fallback metadata.
export const DEFAULT_OG_IMAGE =
  "https://readdy.ai/api/search-image?query=A%20clean%20minimal%20desk%20scene%20with%20a%20laptop%20showing%20an%20organized%20spreadsheet%2C%20notebook%20and%20a%20small%20potted%20plant%2C%20soft%20natural%20daylight%2C%20pastel%20sage%20green%20and%20cream%20tones%2C%20calm%20airy%20editorial%20photography%2C%20no%20text%2C%20no%20watermark&width=1200&height=630&seq=og-image&orientation=landscape";

export function getSiteUrl(): string {
  return import.meta.env.VITE_SITE_URL || FALLBACK_SITE_URL;
}

function upsertMeta(
  attr: "name" | "property",
  key: string,
  content?: string,
): void {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);

  if (content == null) {
    if (el) el.remove();
    return;
  }

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href?: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (href == null) {
    if (el) el.remove();
    return;
  }

  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applySeo(seo: SeoData): void {
  document.title = seo.title;

  upsertMeta("name", "description", seo.description);
  upsertMeta("name", "keywords", seo.keywords);

  const canonical =
    seo.path == null
      ? undefined
      : `${getSiteUrl()}${seo.path === "/" ? "/" : seo.path}`;
  upsertCanonical(canonical);

  upsertMeta("property", "og:title", seo.title);
  upsertMeta("property", "og:description", seo.description);
  upsertMeta("property", "og:url", canonical);
  upsertMeta("property", "og:type", seo.type);
  upsertMeta("property", "og:image", seo.image);

  upsertMeta("name", "twitter:title", seo.title);
  upsertMeta("name", "twitter:description", seo.description);
  upsertMeta("name", "twitter:image", seo.image);
}

export function useSeo(seo: SeoData): void {
  useEffect(() => {
    applySeo(seo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    seo.title,
    seo.description,
    seo.keywords,
    seo.path,
    seo.type,
    seo.image,
  ]);
}